import React, { memo, useCallback, useEffect, useState } from "react";
import { useTranslate, transformDateToLocalDateString } from "helpers";
import { Button } from "ui/components/Button/Button";
import { OfficeEditFormValues, OfficeNoteRow } from "services/api/offices/types";
import { getOfficeNotesAPI, deleteOfficeNoteAPI } from "services/api/offices/api";
import { initialDataWithPagination } from "services/api/consts";
import { ReactSVG } from "react-svg";
import { Loader } from "ui/components/Loader";
import OfficeNoteEditCard from "./OfficeNoteEditCard/OfficeNoteEditCard";
import { Badge } from "ui/components/Badge";

interface OfficeNoteStepProps {
  formValues: OfficeEditFormValues;
}

const OfficeNoteStep: React.FC<OfficeNoteStepProps> = ({ formValues: parentFormValues }) => {
  const officeId = parentFormValues.id;
  const { t } = useTranslate();
  const [notes, setNotes] = useState<OfficeNoteRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editNoteId, setEditNoteId] = useState<string | null>(null);

  const fetchNotes = useCallback(async () => {
    if (!officeId) return;
    setIsLoading(true);
    try {
      const result = await getOfficeNotesAPI(initialDataWithPagination.pagination, officeId);
      setNotes(result.data);
    } catch {
      setNotes([]);
    }
    setIsLoading(false);
  }, [officeId]);

  useEffect(() => {
    if (officeId) fetchNotes();
  }, [officeId]);

  const handleEdit = (noteId: string) => {
    setEditNoteId(noteId);
    setShowEdit(true);
  };

  const handleAddNew = () => {
    setEditNoteId(null);
    setShowEdit(true);
  };

  const handleDelete = async (noteId: string) => {
    if (!officeId) return;
    await deleteOfficeNoteAPI(t, officeId, noteId);
    fetchNotes();
  };

  const handleEditSuccess = () => {
    setShowEdit(false);
    setEditNoteId(null);
    fetchNotes();
  };

  return (
    <>
      <div className="flex items-center justify-center px-4 mt-8 xl:mt-16 min-h-[60vh] xl:min-h-[600px]">
        <div className="w-full max-w-4xl">
          <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">Bilješke poslovnice</h1>
          <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">
            Dodajte bilješke o poslovnici klikom na gumb <strong>"Dodaj bilješku"</strong>.
          </p>

          {isLoading ? (
            <Loader isLoading={true}> </Loader>
          ) : (
            <div className="px-3">
              {notes.length === 0 ? (
                <div className="max-w-md mx-auto">
                  <Button
                    variant="primary"
                    size="full"
                    startIcon="/media/icons/plus.svg"
                    children="Dodaj bilješku"
                    onClick={handleAddNew}
                  />
                </div>
              ) : (
                <>
                  <div className="">
                    {notes.map((note) => (
                      <div key={note.id} className="flex items-start justify-between p-3 bg-slate-50 border border-slate-200 rounded mb-2 last:mb-0">
                        <div className="flex-1">
                          <p className="text-sm whitespace-pre-wrap flex items-center gap-2"><span>{note.content}</span>    {note.important && <Badge text="Važna bilješka" variant="warning" height="small" />}</p>
                          <div className="flex gap-2 mt-2 text-xs text-slate-400">
                            <span>{transformDateToLocalDateString(note.created ? new Date(note.created) : null)}</span>
                          </div>
                        </div>
                        <div className="flex gap-1 ml-2">
                          <button type="button" onClick={() => handleEdit(note.id)} className="p-2 hover:bg-slate-200 rounded-full">
                            <ReactSVG src="/media/icons/edit.svg" className="w-4 h-4 stroke-slate-600" />
                          </button>
                          <button type="button" onClick={() => handleDelete(note.id)} className="p-2 hover:bg-red-100 rounded-full">
                            <ReactSVG src="/media/icons/trash.svg" className="w-4 h-4 stroke-red-600" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end mt-3">
                    <Button variant="primary" startIcon="/media/icons/plus.svg" children="Dodaj bilješku" onClick={handleAddNew} />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <OfficeNoteEditCard
        officeId={officeId ?? ""}
        noteId={editNoteId}
        show={showEdit}
        onSuccess={handleEditSuccess}
        onClose={() => setShowEdit(false)}
      />
    </>
  );
};

export default memo(OfficeNoteStep);
